import * as React from 'react';
import { Box, Chip, Link, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import StripedDataGrid from 'shared/components/StripedDataGrid';
import { ADMIN_HANDLES, KittingAction, MODES, money, OUTCOMES } from '../types';

const OUTCOME_COLOR: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
  [OUTCOMES.WOULD_KIT]: 'info',
  [OUTCOMES.KITTED]: 'success',
  [OUTCOMES.SKIPPED]: 'warning',
  [OUTCOMES.ERROR]: 'error',
};

function componentsTooltip(json: string | null): string {
  if (!json) return '';
  try {
    const parsed = JSON.parse(json) as { sku: string; fromQty: number; toQty: number }[];
    return parsed.map(c => `${c.sku}: ${c.fromQty} → ${c.toQty}`).join('\n');
  } catch {
    return json;
  }
}

// The action history (server-side paged). Every matched order shows up here —
// dry-run and live — with the exact money math the job computed.
export default function HistoryTable({
  rows,
  total,
  loading,
  page,
  pageSize,
  onPageChange,
  onRowClick,
}: {
  rows: KittingAction[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRowClick: (action: KittingAction) => void;
}) {
  const columns = React.useMemo<GridColDef[]>(() => {
    const all: GridColDef[] = [
      {
        field: 'created_at',
        headerName: 'When',
        width: 150,
        valueFormatter: ({ value }) => dayjs(String(value)).format('MMM D, HH:mm:ss'),
      },
      {
        field: 'mode',
        headerName: 'Mode',
        width: 90,
        renderCell: ({ value }) => (
          <Chip label={value === MODES.LIVE ? 'live' : 'dry-run'} size="small" variant="outlined" />
        ),
      },
      {
        field: 'outcome',
        headerName: 'Outcome',
        width: 110,
        renderCell: ({ value }) => (
          <Chip label={String(value)} color={OUTCOME_COLOR[String(value)] ?? 'default'} size="small" />
        ),
      },
      {
        field: 'store',
        headerName: 'Store',
        width: 70,
        renderCell: ({ value }) => <Chip label={String(value)} size="small" variant="outlined" />,
      },
      {
        field: 'order_name',
        headerName: 'Order',
        width: 110,
        renderCell: ({ row }) => (
          <Link
            href={`https://admin.shopify.com/store/${ADMIN_HANDLES[row.store] ?? 'dosaze'}/orders/${row.order_id}`}
            target="_blank"
            rel="noreferrer"
            // Opening Shopify must not also open the detail drawer.
            onClick={event => event.stopPropagation()}
          >
            {row.order_name}
          </Link>
        ),
      },
      { field: 'rule_id', headerName: 'Rule', width: 150 },
      {
        field: 'components_removed',
        headerName: 'Components',
        width: 130,
        sortable: false,
        renderCell: ({ value }) =>
          value ? (
            <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{componentsTooltip(String(value))}</Box>}>
              <Typography variant="body2" sx={{ cursor: 'help' }}>
                {(() => {
                  try {
                    return `${(JSON.parse(String(value)) as unknown[]).length} line(s)`;
                  } catch {
                    return 'view';
                  }
                })()}
              </Typography>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      { field: 'kit_sku', headerName: 'Kit SKU', width: 200 },
      { field: 'times', headerName: 'Kits', width: 70 },
      {
        field: 'order_total_before',
        headerName: 'Order total',
        width: 170,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.order_total_before == null) return '—';
          const unchanged =
            row.order_total_after == null || row.order_total_after === row.order_total_before;
          return (
            <Tooltip
              title={
                unchanged
                  ? 'The customer pays exactly what they paid before — the kit line is discounted to match.'
                  : 'The order total changed — this should never happen; the edit is gated on a zero delta.'
              }
            >
              <Typography variant="body2" color={unchanged ? 'text.primary' : 'error'} sx={{ cursor: 'help' }}>
                {money(row.order_total_before, row.store)} →{' '}
                {money(row.order_total_after ?? row.order_total_before, row.store)}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'paid_amount',
        headerName: 'Paid for items',
        width: 120,
        renderCell: ({ row }) => (
          <Tooltip title="What the customer actually paid for the component lines being replaced (after any promo)">
            <Typography variant="body2" sx={{ cursor: 'help' }}>
              {money(row.paid_amount, row.store)}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'kit_list_price',
        headerName: 'Kit list',
        width: 100,
        renderCell: ({ row }) => (
          <Tooltip title="The kit variant's own price in Shopify before discounting">
            <Typography variant="body2" sx={{ cursor: 'help' }}>
              {money(row.kit_list_price, row.store)}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'discount_applied',
        headerName: 'Discount',
        width: 100,
        renderCell: ({ row }) => (
          <Tooltip title="Kit list − paid for items. Applied to the kit line so the order total does not move.">
            <Typography variant="body2" sx={{ cursor: 'help' }}>
              {money(row.discount_applied, row.store)}
            </Typography>
          </Tooltip>
        ),
      },
      { field: 'reason', headerName: 'Reason', flex: 1, minWidth: 200 },
    ];
    return all;
  }, []);

  const gridRows = rows.map((row, index) => ({
    id: `${row.run_id}-${row.order_id}-${row.rule_id ?? 'none'}-${index}`,
    ...row,
  }));

  return (
    <Box sx={{ height: 560 }}>
      <StripedDataGrid
        rows={gridRows}
        columns={columns}
        loading={loading}
        density="compact"
        disableSelectionOnClick
        paginationMode="server"
        rowCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        rowsPerPageOptions={[pageSize]}
        getRowClassName={params => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        onRowClick={params => onRowClick(params.row as KittingAction)}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />
    </Box>
  );
}
